<template>
  <IndexBase
    @auth="onAuth($event)"
    action="Index"
    controller="User"
  ></IndexBase>

  <Card>
    <template #title>
      <Toolbar>
        <template #left>
          <div class="p-card-title">
            {{ $t("agt_person") }}
          </div>
        </template>
      </Toolbar>
    </template>
    <template #content>
      <DataTable
        ref="dt"
        responsiveLayout="scroll"
        class="p-datatable-sm"
        filterDisplay="row"
        sortMode="multiple"
        removableSort
        stateStorage="local"
        :stateKey="stateKey"
        :value="dataModel"
        :loading="!dataModel || loading"
        :paginator="true"
        :rows="pageSize"
        scrollHeight="70vh"
        wrapperClass="responsive-height"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="[10, 20, 50]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        :lazy="true"
        v-model:first="this.start"
        @state-restore="onStateRestore($event)"
        @page="onPage($event)"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        v-model:filters="filters"
        :totalRecords="totalRecords"
        :rowClass="rowClass"
        dataKey="Id"
        id="dataUser"
      >
        <Column
          :exportable="false"
          :sortable="false"
          headerStyle="white-space:nowrap"
        >
          <template #header="">
            <Button
              v-show="canCreate"
              :label="$t('create')"
              icon="pi pi-plus"
              class="p-button-primary p-button-sm"
              @click="editor(this.$FORM_MODE_CREATE, 0)"
            />
          </template>
          <template #filter>
            <Button
              :label="$t('clear')"
              icon="pi pi-filter-slash"
              class="p-button-outlined p-button-primary p-button-sm"
              @click="clearFilter"
              :badge="filterBadge"
              badgeClass="p-badge-danger"
            />
          </template>
          <template #body="dataModel">
            <Button
              icon="pi pi-search"
              v-show="canView"
              class="p-button-rounded p-button-info mr-2 p-button-sm"
              @click="editor(this.$FORM_MODE_VIEW, dataModel.data.Id)"
              v-tooltip.bottom="'View'"
            />
            <Button
              icon="pi pi-pencil"
              v-show="canEdit && dataModel.data.IsDeleted == 'N'"
              class="p-button-rounded p-button-success mr-2 p-button-sm"
              @click="editor(this.$FORM_MODE_EDIT, dataModel.data.Id)"
              v-tooltip.bottom="'Edit'"
            />
            <Button
              icon="pi pi-trash"
              v-show="canDelete && dataModel.data.IsDeleted == 'N'"
              class="p-button-rounded p-button-danger mr-2 p-button-sm"
              @click="editor(this.$FORM_MODE_DELETE, dataModel.data.Id)"
              v-tooltip.bottom="'Delete'"
            />
          </template>
        </Column>
        <Column field="Username" :header="$t('username')" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              :placeholder="$t('username')"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
            />
          </template>
        </Column>
        <Column field="Fullname" :header="$t('full_name')" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              :placeholder="$t('full_name')"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
            />
          </template>
        </Column>
        <Column
          field="CompanyCode"
          :header="$t('companycode')"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              :placeholder="$t('companycode')"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
            />
          </template>
        </Column>
        <Column field="Email" :header="$t('email')" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              :placeholder="$t('email')"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
            />
          </template>
        </Column>
        <Column
          field="ValidFrom"
          :header="$t('valid_from')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
              :placeholder="$t('valid_from')"
              dateFormat="dd/mm/yyyy"
              inputMask="99/99/9999"
              momentFormat="DD/MM/YYYY"
              class="p-column-filter p-inputtext-sm"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
            />
          </template>
          <template #body="{ data }">
            {{ this.$formatDate(data.ValidFrom) }}
          </template>
        </Column>
        <Column
          field="ValidTo"
          :header="$t('valid_to')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
              :placeholder="$t('valid_to')"
              dateFormat="dd/mm/yyyy"
              inputMask="99/99/9999"
              momentFormat="DD/MM/YYYY"
              class="p-column-filter p-inputtext-sm"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
            />
          </template>
          <template #body="{ data }">
            {{ this.$formatDate(data.ValidTo) }}
          </template>
        </Column>
        <Column
          field="CreatedDate"
          :header="$t('created_date')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
              :placeholder="$t('created_date')"
              dateFormat="dd/mm/yyyy"
              inputMask="99/99/9999"
              momentFormat="DD/MM/YYYY"
              class="p-column-filter p-inputtext-sm"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
            />
          </template>
          <template #body="{ data }">
            {{ this.$formatDateTime(data.CreatedDate) }}
          </template>
        </Column>
        <Column field="CreatedBy" :header="$t('created_by')" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
              placeholder="Created By"
            />
          </template>
        </Column>
        <Column
          field="EditedDate"
          :header="$t('edited_date')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
              dateFormat="dd/mm/yyyy"
              inputMask="99/99/9999"
              momentFormat="DD/MM/YYYY"
              class="p-column-filter p-inputtext-sm"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              placeholder="Edited Date"
            />
          </template>
          <template #body="{ data }">
            {{ this.$formatDateTime(data.EditedDate) }}
          </template>
        </Column>
        <Column field="EditedBy" :header="$t('edited_by')" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
              placeholder="Edited By"
            />
          </template>
        </Column>
        <Column
          field="IsDeleted"
          :header="$t('is_deleted')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <YNSelect
              :placeholder="$t('is_deleted')"
              :useBinary="true"
              v-if="filterModel"
              v-model="filterModel.value"
              @change="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="DeletedDate"
          :header="$t('deleted_date')"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
              dateFormat="dd/mm/yyyy"
              inputMask="99/99/9999"
              momentFormat="DD/MM/YYYY"
              class="p-column-filter p-inputtext-sm"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              placeholder="Edited Date"
            />
          </template>
          <template #body="{ data }">
            {{ this.$formatDateTime(data.DeletedDate) }}
          </template>
        </Column>
        <Column
          field="DeletedBy"
          :header="$t('deleted_by')"
          dataType="date"
          :sortable="true"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              type="text"
              v-if="filterModel"
              v-model="filterModel.value"
              @keydown.enter="filterCallback()"
              class="p-column-filter p-inputtext-sm"
              placeholder="Deleted By"
            />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>

<script>
export default {
  name: "AGT User",
  data() {
    return {
      dataModel: null,
      loading: true,
      totalRecords: 0,
      start: 0,
      stateKey:
        this.$STATE_NAME + "user-agt-" + this.$store.getters.getUsername,
      role: this.$store.getters.getRole,
      isAdmin: this.$store.getters.getIsAdmin,
      isUser: this.$store.getters.getIsUser,
      isTransporter: this.$store.getters.getIsTransporter,
      isDriver: this.$store.getters.getIsDriver,
      sorts: null,
      lazyParams: {},
      filters: null,
      stateFilters: null,
      pageSize: 10,
      editorPath: "/Agt/Editor",
      dataUrl: "agt/list",
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canView: false,
    };
  },
  methods: {
    onAuth(event) {
      this.canCreate = event.canCreate;
      this.canEdit = event.canEdit;
      this.canDelete = event.canDelete;
      this.canView = event.canView;
    },
    onPage(event) {
      this.pageSize = event.rows;
      this.start = event.first;
      this.loadLazyData();
    },
    onSort(event) {
      this.sorts = event.multiSortMeta;
      this.loadLazyData();
    },
    rowClass(data) {
      if (data.IsDeleted == "Y") {
        return "classRed";
      }
    },
    onFilter() {
      this.loadLazyData();
    },
    onSelect(event) {
      this.selectedRow = event.data;
    },
    onRowExpand(event) {
      this.expandedRows = [];
      this.expandedRows.push(event.data);
    },
    editor(mode, id) {
      this.$router.push({
        path: this.editorPath,
        query: { mode: mode, id: id },
      });
    },
    filter(mode) {
      this.$router.push({ path: this.filterPath, query: { mode: mode } });
    },
    sidebarMessage(event) {
      this.$toast.add(event);
    },
    loadLazyData() {
      this.loading = true;
      this.selectedRow = null;

      this.lazyParams = {
        start: this.start,
        pageSize: this.pageSize,
        sorts: this.dataSort,
        filters: this.dataFilter,
      };

      var self = this;

      this.$axios
        .post(this.$API_URL + this.dataUrl, this.lazyParams)
        .then((response) => {
          if (response.data.success) {
            this.dataModel = response.data.data;
            this.totalRecords = response.data.totalRecords;
          } else {
            self.$toast.add({
              severity: "error",
              summary: "Error",
              detail: response.data.message,
              life: 3000,
            });
          }

          this.loading = false;
        })
        .catch(function (err) {
          self.$toast.add({
            severity: "error",
            summary: "Error",
            detail: err.message,
            life: 3000,
          });
          self.loading = false;
        });
    },
    setFilters() {
      this.filters = {};
      var exportable = this.$refs.dt.columns.filter((c) => c.props.field);
      for (var i = 0; i < exportable.length; i++) {
        var filterValue = null;
        if (this.stateFilters) {
          var field = this.stateFilters[exportable[i].props.field];
          if (field) filterValue = field.value;
        }
        this.filters[exportable[i].props.field] = {
          value: filterValue,
          matchMode: "contains",
        };
      }
    },
    onStateRestore(event) {
      this.start = event.first;
      this.pageSize = event.rows;
      this.stateFilters = event.filters;
      this.sorts = event.multiSortMeta;
    },
    editOrder() {
      this.$router.push({
        path: this.editorPath,
        query: { mode: this.$FORM_MODE_EDIT, id: this.selectedRow[0].Id },
      });
    },
    onModalClose() {
      this.loadLazyData();
    },
    clearFilter() {
      this.filters = {};
      var exportable = this.$refs.dt.columns.filter((c) => c.props.field);
      for (var i = 0; i < exportable.length; i++) {
        var fieldName = exportable[i].props.field;
        var filterValue = "";
        if (fieldName.includes("Date")) {
          filterValue = null;
        }
        this.filters[exportable[i].props.field] = {
          value: filterValue,
          matchMode: "contains",
        };
      }
      this.loadLazyData();
    },
  },
  mounted() {
    this.$emit("unblock-ui");
    this.setFilters();
    this.loadLazyData();
    this.$showToast();
  },
  computed: {
    dataFilter() {
      var h = [];
      if (this.filters) {
        for (const [key, value] of Object.entries(this.filters)) {
          h.push({
            field: key ? key : null,
            value: value.value,
            matchMode: value.matchMode,
          });
        }
      }
      return h;
    },
    dataSort() {
      var h = [];
      if (this.sorts) {
        for (var x = 0; x < this.sorts.length; x++) {
          h.push({
            field: this.sorts[x].field,
            order: this.sorts[x].order == 1 ? "ASC" : "DESC",
          });
        }
      }
      return h;
    },
    filterBadge() {
      var badgeNumber = 0;

      this.dataFilter.filter(function (item) {
        if (item.value && item.field != "Status") {
          badgeNumber++;
        }
      });

      return badgeNumber;
    },
  },
};
</script>

<style #scoped>
.p-card .p-card-content {
  margin: 0 !important;
  padding: 0 !important;
}

#dataUser .classRed {
  background-color: #f85656 !important;
}
</style>
